import { TopicFolder } from '@/types/topic';

const fetchTopics = async (workspace: string) => {
    // Validate workspace parameter
    if (!workspace) {
        console.warn('fetchTopics service called without workspace ID');
        return [];
    }

    const response = await fetch('/api/topics/list', {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace }),
    });

    if (!response.ok) {
        console.warn('fetchTopics failed:', response.status, response.statusText);
        return [];
    }

    const responseJson = await response.json();
    return Array.isArray(responseJson) ? responseJson : [];
}

const updateTopic = async (id: string, name: string, description: string) => {
    if (!id) {
        console.warn('updateTopic service called without topic ID');
        throw new Error('Topic ID is required');
    }

    const response = await fetch('/api/topics/edit', {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, name, description }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update topic');
    }

    return await response.json();
}

// Topic Folder Operations

const fetchTopicFolders = async (workspaceId: string): Promise<TopicFolder[]> => {
    if (!workspaceId || workspaceId === 'undefined') {
        console.warn('Cannot fetch topic folders: invalid workspaceId', workspaceId);
        return [];
    }

    const params = new URLSearchParams({ workspace_id: workspaceId });

    const response = await fetch(`/api/topic-folders?${params.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Failed to fetch topic folders');
    }

    return response.json();
};

const createTopicFolder = async (data: {
    name: string;
    workspace_id: string;
    parent_id?: string | null;
}): Promise<TopicFolder> => {
    const response = await fetch('/api/topic-folders', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create topic folder');
    }

    return response.json();
};

const updateTopicFolder = async (
    id: string,
    data: { name?: string; parent_id?: string | null }
): Promise<TopicFolder> => {
    const response = await fetch(`/api/topic-folders/${id}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update topic folder');
    }

    return response.json();
};

const deleteTopicFolder = async (id: string): Promise<void> => {
    const response = await fetch(`/api/topic-folders/${id}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete topic folder');
    }
};

const moveTopicToFolder = async (
    topicId: string,
    folderId: string | null
): Promise<void> => {
    const response = await fetch(`/api/topics/${topicId}/folder`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folder_id: folderId }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to move topic to folder');
    }
};

export const topicsService = {
    fetchTopics,
    updateTopic,
    fetchTopicFolders,
    createTopicFolder,
    updateTopicFolder,
    deleteTopicFolder,
    moveTopicToFolder,
}